// Shared with assets/market-mismatch.js — picking a market by hand counts as
// answering "are you in the right place?", so the prompt must not fire on the
// page we land on. Keep this key in sync with DISMISS_KEY over there.
//
// `var`, not `const`, on purpose. Everything below is already written to
// survive being evaluated twice (the customElements.get guard), but a top-level
// `const` throws on the SECOND evaluation — before that guard is ever reached —
// and takes the whole file down with it. That is exactly what happened while
// the <script src> sat in snippets/header-localization.liquid and rendered
// three times: runs 2 and 3 died on
//     SyntaxError: Identifier 'MARKET_MISMATCH_DISMISS_KEY' has already been declared
// so the footer and mobile-drawer switchers never registered, with no visible
// symptom beyond a console entry. The tag now emits once from
// layout/theme.liquid, which fixes the cause; `var` makes redeclaration
// harmless so a future second include degrades to a no-op instead of a
// breakage. Do not "modernise" this back to const/let.
var MARKET_MISMATCH_DISMISS_KEY = 'marketMismatchDismissed';

if (!customElements.get('header-localization')) {
  customElements.define(
    'header-localization',
    class HeaderLocalization extends HTMLElement {
      constructor() {
        super();
        this.toggle = this.querySelector('.header-localization__toggle');
        this.panel = this.querySelector('.header-localization__panel');
        this.form = this.querySelector('.header-localization__form');
        this.countryInput = this.querySelector('input[name="country_code"]');
        this.localeInput = this.querySelector('input[name="locale_code"]');
        this.search = this.querySelector('.header-localization__search-input');
        this.options = Array.from(
          this.querySelectorAll('button.header-localization__country, button.header-localization__lang')
        );
        this.items = Array.from(this.querySelectorAll('.header-localization__item'));
        this.list = this.querySelector('.header-localization__list');
        this.listWrap = this.querySelector('.header-localization__list-wrap');

        // Inline mode (e.g. inside the mobile nav drawer): no toggle button —
        // the host panel controls visibility, so the open/close machinery is skipped.
        this.inline = !this.toggle;

        // Pending [hidden] re-apply from a closing fade (see close()).
        this.hideTimer = null;

        // Active store locale (BCP-47), emitted by the Liquid snippet from
        // request.locale.iso_code. Falls back to <html lang> then English so a
        // missing attribute never breaks country-name resolution.
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';

        this.translateCountryNames();

        if (this.toggle) this.toggle.addEventListener('click', this.togglePanel.bind(this));
        this.options.forEach((opt) => opt.addEventListener('click', this.onOptionClick.bind(this)));
        if (this.search) this.search.addEventListener('input', this.onFilter.bind(this));
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        if (!this.inline) {
          document.addEventListener('click', this.onDocumentClick);
          document.addEventListener('keydown', this.onKeyDown);
        }

        // Bottom scroll-fade hint: flag the list-wrap while the list can still
        // scroll down so a gradient can signal there are more countries below.
        this.updateScrollHint = this.updateScrollHint.bind(this);
        if (this.list && this.listWrap) {
          this.list.addEventListener('scroll', this.updateScrollHint, { passive: true });
          if (typeof ResizeObserver === 'function') {
            this.scrollHintObserver = new ResizeObserver(this.updateScrollHint);
            this.scrollHintObserver.observe(this.list);
          }
        }
      }

      // Build an Intl.DisplayNames for region names in the store's active
      // locale, degrading gracefully: unsupported runtime → null (callers keep
      // the ISO code), unsupported/invalid locale tag → English.
      regionDisplayNames() {
        if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return null;
        try {
          return new Intl.DisplayNames([this.locale, 'en'], { type: 'region' });
        } catch (e) {
          try {
            return new Intl.DisplayNames(['en'], { type: 'region' });
          } catch (e2) {
            return null;
          }
        }
      }

      translateCountryNames() {
        const displayNames = this.regionDisplayNames();
        if (!displayNames) return;
        this.querySelectorAll('.header-localization__name[data-country-code]').forEach((el) => {
          if (el.classList.contains('header-localization__name--current')) return;
          const code = (el.dataset.countryCode || '').toUpperCase();
          if (code.length !== 2) return;
          const localized = displayNames.of(code);
          if (localized) el.textContent = localized;
        });
      }

      disconnectedCallback() {
        if (this.scrollHintObserver) this.scrollHintObserver.disconnect();
        if (this.hideTimer) clearTimeout(this.hideTimer);
        if (this.inline) return;
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeyDown);
      }

      updateScrollHint() {
        if (!this.list || !this.listWrap) return;
        const canScrollDown = this.list.scrollHeight - this.list.clientHeight - this.list.scrollTop > 2;
        this.listWrap.classList.toggle('has-scroll-hint', canScrollDown);
      }

      togglePanel() {
        if (this.inline) return;
        if (this.toggle.getAttribute('aria-expanded') === 'true') this.close();
        else this.open();
      }

      open() {
        this.toggle.setAttribute('aria-expanded', 'true');
        // A close() fade may still be running — cancel its pending re-hide so
        // it can't yank the panel away mid-open.
        if (this.hideTimer) {
          clearTimeout(this.hideTimer);
          this.hideTimer = null;
        }
        this.panel.hidden = false;
        // Commit the un-hidden frame BEFORE adding .is-open — display:none →
        // flex and opacity 0 → 1 in the same style pass would skip the fade.
        this.panel.getBoundingClientRect();
        this.panel.classList.add('is-open');
        if (this.search) {
          this.search.value = '';
          this.applyFilter('');
          requestAnimationFrame(() => this.search.focus());
        }
      }

      close() {
        this.toggle.setAttribute('aria-expanded', 'false');
        this.panel.classList.remove('is-open');
        // Keep the panel in the tree while the opacity fade plays; [hidden]
        // lands after. Duration is read from the computed transition so the
        // CSS (section-header.css) stays the single source of truth — 0s under
        // prefers-reduced-motion, i.e. it hides immediately.
        const fadeMs = parseFloat(getComputedStyle(this.panel).transitionDuration) * 1000 || 0;
        this.hideTimer = setTimeout(() => {
          this.panel.hidden = true;
          this.hideTimer = null;
        }, fadeMs);
      }

      onDocumentClick(event) {
        if (this.toggle.getAttribute('aria-expanded') !== 'true') return;
        if (!this.contains(event.target)) this.close();
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
          this.close();
          this.toggle.focus();
        }
      }

      onFilter() {
        this.applyFilter(this.search.value.toLowerCase().trim());
      }

      applyFilter(value) {
        this.items.forEach((item) => {
          const name = item.querySelector('.header-localization__name')?.textContent.toLowerCase() || '';
          const langs = Array.from(item.querySelectorAll('.header-localization__lang'))
            .map((el) => el.textContent.toLowerCase())
            .join(' ');
          const match = !value || name.includes(value) || langs.includes(value);
          item.hidden = !match;
        });
        this.updateScrollHint();
      }

      // Record that the visitor chose a market themselves, so the mismatch
      // drawer stays quiet on the page they land on (and for the rest of the
      // session). Storage may be blocked in private mode — non-fatal, the
      // prompt just behaves as it did before.
      markMismatchDismissed() {
        try {
          sessionStorage.setItem(MARKET_MISMATCH_DISMISS_KEY, '1');
        } catch (e) {
          // Ignore — worst case the prompt appears once after switching.
        }
      }

      onOptionClick(event) {
        const button = event.currentTarget;
        if (button.dataset.redirectUrl) {
          this.markMismatchDismissed();
          window.location.href = button.dataset.redirectUrl;
          return;
        }
        if (button.dataset.international === 'true') {
          this.submitInternational(button);
          return;
        }
        if (!button.dataset.countryCode || !button.dataset.localeCode) return;

        let countryCode = button.dataset.countryCode;
        let localeCode = button.dataset.localeCode;

        // Country-row clicks default to the market's first language. For DACH,
        // that was often EN, which can normalise back to Austria in the same
        // market. Prefer DE when the row offers it.
        if (button.classList.contains('header-localization__country')) {
          const item = button.closest('.header-localization__item');
          const germanLang = item?.querySelector(
            '.header-localization__lang[data-locale-code="de"], .header-localization__lang[data-locale-code^="de-"]'
          );
          if (germanLang && ['DE', 'AT', 'CH'].includes(countryCode.toUpperCase())) {
            localeCode = germanLang.dataset.localeCode;
          }
        }

        this.countryInput.value = countryCode;
        this.localeInput.value = localeCode;
        this.markMismatchDismissed();
        this.form.submit();
      }

      async submitInternational(button) {
        const pairs = (button.dataset.countryCodes || '').split(',').filter(Boolean);
        if (pairs.length === 0) return;
        const pairMap = pairs.reduce((map, pair) => {
          const [code, locale] = pair.split(':');
          if (code && locale) map[code.toUpperCase()] = locale;
          return map;
        }, {});
        const codes = Object.keys(pairMap);
        if (codes.length === 0) return;

        let target = null;
        try {
          const res = await fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
          if (res.ok) {
            const data = await res.json();
            const detected = (data?.detected_values?.country?.handle || '').toUpperCase();
            if (detected && pairMap[detected]) target = detected;
          }
        } catch (e) {
          // Geo endpoint unreachable — fall through to alphabetical default.
        }

        if (!target) {
          if (pairMap.DE) {
            target = 'DE';
          } else {
            const dn = this.regionDisplayNames();
            target = codes
              .map((code) => ({ code, name: dn ? dn.of(code) || code : code }))
              .sort((a, b) => a.name.localeCompare(b.name, this.locale))[0].code;
          }
        }

        this.countryInput.value = target;
        this.localeInput.value = pairMap[target];
        this.markMismatchDismissed();
        this.form.submit();
      }
    }
  );
}
